// The visitor's live connection for the website chat: new messages for their chat (the team's replies, the
// AI's answers) arrive the moment they are saved, over a WebSocket (GET /api/chat/live, served by the
// ChatHub Durable Object). One connection per page, shared by the chat button and the chat window.
//
// It reconnects by itself (1s, 2s, 4s … up to 30s) and fetches anything missed while it was down. Only when a
// live connection cannot be made at all (a site without the hub) does it check every 20 seconds instead.
import { api } from './api.js';
import { loadChat, forgetChat } from './chat-store.js';

const PING_MS = 50000;      // keeps the connection open; answered by Cloudflare without waking anything
const FALLBACK_MS = 20000;
const GIVE_UP_AFTER = 8;    // failed attempts in a row before relying on the fallback alone

const listeners = new Set();
let socket = null;
let wanted = false;
let failures = 0;
let retryTimer = null;
let pingTimer = null;
let fallbackTimer = null;

const emit = (event) => listeners.forEach((fn) => fn(event));

// fn({ type: 'messages', messages }) | fn({ type: 'mode', team }) | fn({ type: 'live', live })
export function onChat(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Everything after the last message this browser has seen.
export async function catchUp(after) {
  const chat = loadChat();
  if (!chat) return;
  try {
    const data = await api(`/api/chat?after=${after ?? chat.seen ?? 0}`, { headers: { 'X-Chat-Key': chat.key } });
    if (data.messages?.length) emit({ type: 'messages', messages: data.messages });
    emit({ type: 'mode', team: data.team });
  } catch (err) {
    if (err.status === 404) { forgetChat(); stopLive(); }
  }
}

function startFallback() {
  if (!fallbackTimer) fallbackTimer = setInterval(() => { if (document.visibilityState === 'visible') catchUp(); }, FALLBACK_MS);
}
function stopFallback() { clearInterval(fallbackTimer); fallbackTimer = null; }

function connect() {
  retryTimer = null;
  const chat = loadChat();
  if (!wanted || !chat || socket) return;
  let ws;
  try {
    ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/chat/live`, ['kg-chat.v1', `key.${chat.key}`]);
  } catch { startFallback(); return; }
  socket = ws;
  ws.onopen = () => {
    failures = 0;
    stopFallback();
    emit({ type: 'live', live: true });
    catchUp();
    pingTimer = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, PING_MS);
  };
  ws.onmessage = (e) => {
    if (e.data === 'pong') return;
    let event;
    try { event = JSON.parse(e.data); } catch { return; }
    if (event.type === 'message') emit({ type: 'messages', messages: [event.message] });
    else if (event.type === 'mode') emit(event);
  };
  ws.onclose = () => {
    clearInterval(pingTimer);
    if (socket === ws) socket = null;
    emit({ type: 'live', live: false });
    if (!wanted) return;
    startFallback();
    if (++failures <= GIVE_UP_AFTER) retryTimer = setTimeout(connect, Math.min(30000, 1000 * 2 ** (failures - 1)));
  };
}

// Opens the live connection for this browser's chat (no chat yet: nothing to do).
export function startLive() {
  if (typeof WebSocket === 'undefined' || !loadChat()) return;
  wanted = true;
  if (!socket && !retryTimer) { failures = 0; connect(); }
}

export function stopLive() {
  wanted = false;
  clearTimeout(retryTimer); retryTimer = null;
  stopFallback();
  if (socket) { const ws = socket; socket = null; ws.close(1000); }
}
